import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { mainTheme } from '../Theme';
import jwt_decode from 'jwt-decode';
import { client } from '..';
import { RefreshTokenMutation, TokenResponse } from './Login';
import { UserFeature } from '../types/UserFeature';

const ONE_HOUR_MS = 60 * 60 * 1000;
const TEN_MINUTES_MS = 10 * 60 * 1000;
const REFRESH_BEFORE_MS = ONE_HOUR_MS + 5 * 60 * 1000; // 1h5m before expiry

 // For testing (30s and 15s)
// const ONE_HOUR_MS = 25 * 60 * 60 * 1000;
// const TEN_MINUTES_MS = 25 * 60 * 60 * 1000 - 10 * 1000;  // 10s after first
// const TEN_MINUTES_MS = 10 * 60 * 1000;
// const REFRESH_BEFORE_MS = 24 * 60 * 60 * 1000 - 10 * 1000; // TEST: fires ~10s after login    

type Phase = 'none' | 'first' | 'second' | 'expired';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        dialogTitle: {
            color: mainTheme.TemplateColor.Primary,
            '& .MuiTypography-root': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }
        },
        warningContent: {
            backgroundColor: '#FFF3E0',
            '& .MuiDialogContentText-root': {
                color: '#000000ff',
            }
        },
        urgentContent: {
            backgroundColor: '#FFF8E1',
            '& .MuiDialogContentText-root': {
                color: '#F57C00',
            }
        },
        camelCaseButton: {
            textTransform: 'none'
        }
    })
);

const SessionExpiryWarningDialog: React.FC = () => {
    const classes = useStyles();
    const [phase, setPhase] = useState<Phase>('none');
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const isRefreshingRef = useRef(false);

    const handleLogout = useCallback(() => {
        const tenantId = sessionStorage.getItem('tenantId') || '';
        window.location.href = '/' + tenantId + '/logout';
    // tslint:disable-next-line: align
    }, []);

    const clearAllTimers = useCallback(() => {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];
    // tslint:disable-next-line: align
    }, []);

    const setupTimers = useCallback((expiryMs: number) => {
        clearAllTimers();

        const now = Date.now();
        const timeUntilExpiry = expiryMs - now;

        if (timeUntilExpiry <= 0) {
            attemptRefresh();
            return;
        }

        const timers = timersRef.current;

        // Refresh timer: fires REFRESH_BEFORE_MS before expiry
        const timeUntilRefresh = timeUntilExpiry - REFRESH_BEFORE_MS;
        if (timeUntilRefresh > 0) {
            timers.push(setTimeout(() => attemptRefresh(), timeUntilRefresh));
        }

        // First warning: 1 hour before expiry
        const timeUntilFirstWarning = timeUntilExpiry - ONE_HOUR_MS;
        if (timeUntilFirstWarning <= 0) {
            setPhase('first');
        } else {
            timers.push(setTimeout(() => setPhase('first'), timeUntilFirstWarning));
        }

        // Second warning: 10 minutes before expiry
        const timeUntilSecondWarning = timeUntilExpiry - TEN_MINUTES_MS;
        if (timeUntilSecondWarning <= 0) {
            setPhase('second');
        } else {
            timers.push(setTimeout(() => setPhase('second'), timeUntilSecondWarning));
        }

        // Expiry: attempt refresh before forcing logout
        timers.push(setTimeout(() => attemptRefresh(), timeUntilExpiry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // tslint:disable-next-line: align
    }, [clearAllTimers, handleLogout]);

    const attemptRefresh = useCallback(async () => {
        // Prevent concurrent refresh attempts (e.g. timer + visibilitychange both firing on wake from sleep)
        if (isRefreshingRef.current) {
            return;
        }
        isRefreshingRef.current = true;

        try {
            const refreshToken = sessionStorage.getItem('refreshTokenForAutoRefresh');
            const tenantId = sessionStorage.getItem('tenantId');
            if (!refreshToken || !tenantId) {
                return;
            }

            const results = await client.mutate<{ refreshToken: TokenResponse }>({
                mutation: RefreshTokenMutation,
                variables: {
                    tenantId: tenantId,
                    refreshToken: refreshToken
                }
            });

            const response = results.data?.refreshToken;
            if (response && response.statusCode === 200) {
                sessionStorage.setItem('accessToken', response.accessToken);
                sessionStorage.setItem('refreshTokenForAutoRefresh', response.refreshToken);
                UserFeature.Init();

                let decoded: { exp?: number };
                try {
                    decoded = jwt_decode(response.accessToken);
                } catch {
                    return;
                }

                if (decoded.exp) {
                    setPhase('none');
                    setupTimers(decoded.exp * 1000);
                }
            } else {
                // Refresh failed — check if token is already expired
                const accessToken = sessionStorage.getItem('accessToken');
                if (accessToken) {
                    try {
                        const decoded: { exp?: number } = jwt_decode(accessToken);
                        if (decoded.exp && decoded.exp * 1000 <= Date.now()) {
                            handleLogout();
                        }
                    } catch {
                        // Do nothing — warning timers will handle it
                    }
                }
            }
        } catch {
            // Silently swallow errors — warning timers will handle expiry
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                if (accessToken) {
                    const decoded: { exp?: number } = jwt_decode(accessToken);
                    if (decoded.exp && decoded.exp * 1000 <= Date.now()) {
                        handleLogout();
                    }
                }
            } catch {
                // Do nothing
            }
        } finally {
            isRefreshingRef.current = false;
        }
    // tslint:disable-next-line: align
    }, [handleLogout, setupTimers]);

    useEffect(() => {
        const accessToken = sessionStorage.getItem('accessToken');
        if (!accessToken) {
            return;
        }

        let decoded: { exp?: number };
        try {
            decoded = jwt_decode(accessToken);
        } catch {
            return;
        }

        if (!decoded.exp) {
            return;
        }

        const expiryMs = decoded.exp * 1000;
        const now = Date.now();
        const timeUntilExpiry = expiryMs - now;

        if (timeUntilExpiry <= 0) {
            // Token already expired — attempt refresh before logging out
            attemptRefresh();
            return;
        }

        setupTimers(expiryMs);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const token = sessionStorage.getItem('accessToken');
                if (!token) {
                    handleLogout();
                    return;
                }

                let decodedToken: { exp?: number };
                try {
                    decodedToken = jwt_decode(token);
                } catch {
                    handleLogout();
                    return;
                }

                if (!decodedToken.exp) {
                    handleLogout();
                    return;
                }

                const tokenExpiryMs = decodedToken.exp * 1000;
                if (tokenExpiryMs <= Date.now()) {
                    clearAllTimers();
                    attemptRefresh();
                } else {
                    setupTimers(tokenExpiryMs);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearAllTimers();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // tslint:disable-next-line: align
    }, []);

    const handleDismiss = () => {
        setPhase('none');
    };

    if (phase === 'none' || phase === 'expired') {
        return null;
    }

    const isFirstWarning = phase === 'first';

    return (
        <React.Fragment>
            <Dialog
                open={true}
                onClose={handleDismiss}
                className={classes.root}
                maxWidth="sm"
                fullWidth={true}
            >
                <DialogTitle
                    id="form-dialog-title"
                    className={classes.dialogTitle}
                >
                    <div>{isFirstWarning ? 'Session Expiring Soon' : 'Session About to Expire'}</div>
                </DialogTitle>
                <DialogContent dividers={true} className={isFirstWarning ? classes.warningContent : classes.urgentContent}>
                    <DialogContentText>
                        {isFirstWarning
                            ? 'Your session will expire in approximately 1 hour. Please save any unsaved work and sign out to continue working.'
                            : 'Your session will expire in approximately 10 minutes. Any unsaved work may be lost. Please save your work immediately and sign in again.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDismiss}
                        color="primary"
                    >
                        Continue Working
                    </Button>
                    <Button
                        onClick={handleLogout}
                        color="primary"
                    >
                        Sign Out
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default SessionExpiryWarningDialog;
